import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import '../styles/Settings.css';

// Persona options with descriptions
const PERSONA_OPTIONS = [
  { value: 'investor', label: 'Investor', description: 'Forward-looking signals, risk assessment, portfolio-relevant movements' },
  { value: 'economist', label: 'Economist', description: 'Structural analysis, cross-component relationships, regime coherence' },
  { value: 'policymaker', label: 'Policymaker', description: 'Sector stability, early warning, employment proxies, trade vulnerability' },
  { value: 'journalist', label: 'Journalist', description: 'Story angles, contrasts, turning points, narrative tension' },
  { value: 'analyst', label: 'Analyst', description: 'Granular data, statistical context, exportable datasets, correlation metrics' },
  { value: 'student', label: 'Student', description: 'Educational context, explanations, historical parallels' }
];

function Settings() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('general');

  // Persona state
  const [persona, setPersona] = useState('investor');
  const [personaLoading, setPersonaLoading] = useState(false);

  // Email form state
  const [newEmail, setNewEmail] = useState('');
  const [emailLoading, setEmailLoading] = useState(false);

  // Password form state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);

  // Photo upload state
  const [photoLoading, setPhotoLoading] = useState(false);

  // Preferences state
  const [preferences, setPreferences] = useState({
    email_notifications: false,
    breakpoint_alerts: false,
    weekly_summary: false,
    breakpoint_sectors: [],
    breakpoint_regions: [],
    breakpoint_countries: []
  });
  const [preferencesLoading, setPreferencesLoading] = useState(false);

  // Filter options from backend
  const [filterOptions, setFilterOptions] = useState({
    sectors: [],
    regions: [],
    countries: []
  });

  const API_URL = 'https://api.deepecon.info/api';

  useEffect(() => {
    fetchUserData();
    fetchPreferences();
    fetchFilterOptions();
  }, []);

  const fetchUserData = async () => {
    try {
      const response = await fetch(`${API_URL}/auth/user`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setUser(data.user);
        setNewEmail(data.user.email || '');
        // Set persona from user data
        if (data.user.inferred_persona) {
          setPersona(data.user.inferred_persona);
        }
      } else {
        setError('Failed to load user data');
      }
    } catch (error) {
      console.error('Error fetching user data:', error);
      setError('Connection error');
    } finally {
      setLoading(false);
    }
  };

  const fetchPreferences = async () => {
    try {
      const response = await fetch(`${API_URL}/user/preferences`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setPreferences({
          email_notifications: data.preferences.email_notifications || false,
          breakpoint_alerts: data.preferences.breakpoint_alerts || false,
          weekly_summary: data.preferences.weekly_summary || false,
          breakpoint_sectors: data.preferences.breakpoint_sectors || [],
          breakpoint_regions: data.preferences.breakpoint_regions || [],
          breakpoint_countries: data.preferences.breakpoint_countries || []
        });
      }
    } catch (error) {
      console.error('Error fetching preferences:', error);
    }
  };

  const fetchFilterOptions = async () => {
    try {
      const response = await fetch(`${API_URL}/user/filter-options`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setFilterOptions(data);
      }
    } catch (error) {
      console.error('Error fetching filter options:', error);
    }
  };

  const handleUpdateEmail = async (e) => {
    e.preventDefault();
    setEmailLoading(true);
    setMessage('');
    setError('');

    try {
      const response = await fetch(`${API_URL}/user/update-email`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: newEmail })
      });

      const data = await response.json();

      if (response.ok) {
        setMessage('Email updated successfully!');
        setUser(data.user);
      } else {
        setError(data.error || 'Failed to update email');
      }
    } catch (error) {
      setError('Connection error. Please try again.');
    } finally {
      setEmailLoading(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPasswordLoading(true);
    setMessage('');
    setError('');

    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters');
      setPasswordLoading(false);
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      setPasswordLoading(false);
      return;
    }

    try {
      const response = await fetch(`${API_URL}/user/change-password`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          currentPassword,
          newPassword
        })
      });

      const data = await response.json();

      if (response.ok) {
        setMessage('Password changed successfully!');
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
      } else {
        setError(data.error || 'Failed to change password');
      }
    } catch (error) {
      setError('Connection error. Please try again.');
    } finally {
      setPasswordLoading(false);
    }
  };

  const handlePhotoUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setError('Invalid file type. Allowed: PNG, JPG, GIF, WebP');
      return;
    }

    // Validate file size (1MB max)
    if (file.size > 1 * 1024 * 1024) {
      setError('File too large. Maximum size is 1MB');
      return;
    }

    setPhotoLoading(true);
    setMessage('');
    setError('');

    try {
      const formData = new FormData();
      formData.append('photo', file);

      const response = await fetch(`${API_URL}/user/upload-photo`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });

      const data = await response.json();

      if (response.ok) {
        setMessage('Photo uploaded successfully!');
        setUser(data.user);
      } else {
        setError(data.error || 'Failed to upload photo');
      }
    } catch (error) {
      setError('Connection error. Please try again.');
    } finally {
      setPhotoLoading(false);
      // Reset the file input
      event.target.value = '';
    }
  };

  const handlePhotoRemove = async () => {
    if (!user?.profile_photo) return;

    setPhotoLoading(true);
    setMessage('');
    setError('');

    try {
      const response = await fetch(`${API_URL}/user/delete-photo`, {
        method: 'POST',
        credentials: 'include'
      });

      const data = await response.json();

      if (response.ok) {
        setMessage('Photo removed successfully!');
        setUser(data.user);
      } else {
        setError(data.error || 'Failed to remove photo');
      }
    } catch (error) {
      setError('Connection error. Please try again.');
    } finally {
      setPhotoLoading(false);
    }
  };

  const handlePersonaChange = async (newPersona) => {
    setPersonaLoading(true);
    setMessage('');
    setError('');

    try {
      const response = await fetch(`${API_URL}/user/update-persona`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ persona: newPersona })
      });

      const data = await response.json();

      if (response.ok) {
        setPersona(newPersona);
        setMessage('Persona updated successfully! Your dashboard briefings will now be tailored accordingly.');
      } else {
        setError(data.error || 'Failed to update persona');
      }
    } catch (error) {
      setError('Connection error. Please try again.');
    } finally {
      setPersonaLoading(false);
    }
  };

  const handlePreferenceChange = async (key) => {
    const newPreferences = {
      ...preferences,
      [key]: !preferences[key]
    };
    setPreferences(newPreferences);
    await savePreferences(newPreferences);
  };

  const handleFilterChange = async (filterType, value) => {
    const currentFilters = preferences[filterType] || [];
    let newFilters;

    if (currentFilters.includes(value)) {
      newFilters = currentFilters.filter(v => v !== value);
    } else {
      newFilters = [...currentFilters, value];
    }

    const newPreferences = {
      ...preferences,
      [filterType]: newFilters
    };
    setPreferences(newPreferences);
    await savePreferences(newPreferences);
  };

  const savePreferences = async (newPreferences) => {
    setPreferencesLoading(true);
    setMessage('');
    setError('');

    try {
      const response = await fetch(`${API_URL}/user/preferences`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newPreferences)
      });

      const data = await response.json();

      if (response.ok) {
        setMessage('Preferences saved!');
        setPreferences({
          email_notifications: data.preferences.email_notifications || false,
          breakpoint_alerts: data.preferences.breakpoint_alerts || false,
          weekly_summary: data.preferences.weekly_summary || false,
          breakpoint_sectors: data.preferences.breakpoint_sectors || [],
          breakpoint_regions: data.preferences.breakpoint_regions || [],
          breakpoint_countries: data.preferences.breakpoint_countries || []
        });
      } else {
        setError(data.error || 'Failed to update preferences');
      }
    } catch (error) {
      setError('Connection error. Please try again.');
    } finally {
      setPreferencesLoading(false);
    }
  };

  // Multi-select component
  const MultiSelect = ({ label, options, selected, filterType }) => {
    const [isOpen, setIsOpen] = useState(false);

    return (
      <div className="settings-multiselect">
        <label className="settings-multiselect__label">{label}</label>
        <div style={{ position: 'relative' }}>
          <div
            onClick={() => setIsOpen(!isOpen)}
            className="settings-multiselect__trigger"
          >
            <span className={selected.length === 0 ? 'settings-multiselect__placeholder' : ''}>
              {selected.length === 0 ? `Select ${label.toLowerCase()}...` : `${selected.length} selected`}
            </span>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d={isOpen ? "M18 15l-6-6-6 6" : "M6 9l6 6 6-6"} />
            </svg>
          </div>

          {isOpen && (
            <div className="settings-multiselect__dropdown">
              {options.map(option => (
                <label
                  key={option}
                  className={`settings-multiselect__option ${selected.includes(option) ? 'settings-multiselect__option--selected' : ''}`}
                >
                  <input
                    type="checkbox"
                    checked={selected.includes(option)}
                    onChange={() => handleFilterChange(filterType, option)}
                    style={{ marginRight: '10px' }}
                  />
                  {option}
                </label>
              ))}
            </div>
          )}
        </div>

        {selected.length > 0 && (
          <div className="settings-multiselect__tags">
            {selected.map(item => (
              <span key={item} className="settings-multiselect__tag">
                {item}
                <button
                  onClick={() => handleFilterChange(filterType, item)}
                  className="settings-multiselect__tag-remove"
                >
                  &times;
                </button>
              </span>
            ))}
          </div>
        )}
      </div>
    );
  };

  // Icons
  const DashboardIcon = () => (
    <svg className="settings-sidebar__nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="3" y="3" width="7" height="7" rx="1" />
      <rect x="14" y="3" width="7" height="7" rx="1" />
      <rect x="3" y="14" width="7" height="7" rx="1" />
      <rect x="14" y="14" width="7" height="7" rx="1" />
    </svg>
  );

  const ChartIcon = () => (
    <svg className="settings-sidebar__nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M3 3v18h18" />
      <path d="M18 17V9" />
      <path d="M13 17V5" />
      <path d="M8 17v-3" />
    </svg>
  );

  const NewsIcon = () => (
    <svg className="settings-sidebar__nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M4 22h16a2 2 0 002-2V4a2 2 0 00-2-2H8a2 2 0 00-2 2v16a2 2 0 01-2 2zm0 0a2 2 0 01-2-2v-9c0-1.1.9-2 2-2h2" />
      <path d="M18 14h-8M15 18h-5M10 6h8v4h-8z" />
    </svg>
  );

  const SettingsIcon = () => (
    <svg className="settings-sidebar__nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-2 2 2 2 0 01-2-2v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83 0 2 2 0 010-2.83l.06-.06a1.65 1.65 0 00.33-1.82 1.65 1.65 0 00-1.51-1H3a2 2 0 01-2-2 2 2 0 012-2h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 010-2.83 2 2 0 012.83 0l.06.06a1.65 1.65 0 001.82.33H9a1.65 1.65 0 001-1.51V3a2 2 0 012-2 2 2 0 012 2v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 0 2 2 0 010 2.83l-.06.06a1.65 1.65 0 00-.33 1.82V9a1.65 1.65 0 001.51 1H21a2 2 0 012 2 2 2 0 01-2 2h-.09a1.65 1.65 0 00-1.51 1z" />
    </svg>
  );

  const SupportIcon = () => (
    <svg className="settings-sidebar__nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="10" />
      <path d="M9.09 9a3 3 0 015.83 1c0 2-3 3-3 3" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  );

  if (loading) {
    return (
      <div className="settings-layout">
        <div className="settings-loading">
          <div className="settings-loading__spinner"></div>
          <p>Loading settings...</p>
        </div>
      </div>
    );
  }

  const getInitials = (name) => {
    if (!name) return 'U';
    return name.charAt(0).toUpperCase();
  };

  return (
    <div className="settings-layout">
      {/* Sidebar */}
      <aside className="settings-sidebar">
        <div className="settings-sidebar__logo">
          <div className="settings-sidebar__logo-icon">DE</div>
          <span className="settings-sidebar__logo-text">DeepEcon</span>
        </div>

        <div className="settings-sidebar__section">
          <div className="settings-sidebar__section-title">Platform</div>
          <nav className="settings-sidebar__nav">
            <Link to="/dashboard" className="settings-sidebar__nav-item">
              <DashboardIcon />
              Dashboard
            </Link>
            <a href="https://deepecon.info/sentiment_tracker/" className="settings-sidebar__nav-item">
              <ChartIcon />
              Sentiment Tracker
            </a>
            <a href="https://deepecon.info/economic_desk/" className="settings-sidebar__nav-item">
              <NewsIcon />
              Economic Desk
            </a>
          </nav>
        </div>

        <div className="settings-sidebar__section">
          <div className="settings-sidebar__section-title">Account</div>
          <nav className="settings-sidebar__nav">
            <span className="settings-sidebar__nav-item settings-sidebar__nav-item--active">
              <SettingsIcon />
              Settings
            </span>
            <a href="mailto:support@deepecon.info" className="settings-sidebar__nav-item">
              <SupportIcon />
              Support
            </a>
          </nav>
        </div>

        {/* User info at bottom */}
        {user && (
          <div className="settings-user">
            <div className="settings-user__avatar">
              {user.profile_photo ? (
                <img src={user.profile_photo} alt={user.username} />
              ) : (
                getInitials(user.username)
              )}
            </div>
            <div className="settings-user__info">
              <div className="settings-user__name">{user.username}</div>
              <div className="settings-user__plan">Free Plan</div>
            </div>
          </div>
        )}
      </aside>

      {/* Main Content */}
      <main className="settings-main">
        {/* Breadcrumb */}
        <nav className="settings-breadcrumb">
          <Link to="/dashboard">Dashboard</Link>
          <span className="settings-breadcrumb__separator">/</span>
          <span className="settings-breadcrumb__current">Settings</span>
        </nav>

        {/* Header */}
        <header className="settings-header">
          <h1 className="settings-header__title">Settings</h1>
          <p className="settings-header__subtitle">Manage your account settings and preferences</p>
        </header>

        {/* Tabs */}
        <div className="settings-tabs">
          <button
            className={`settings-tabs__tab ${activeTab === 'general' ? 'settings-tabs__tab--active' : ''}`}
            onClick={() => setActiveTab('general')}
          >
            General
          </button>
          <button
            className={`settings-tabs__tab ${activeTab === 'notifications' ? 'settings-tabs__tab--active' : ''}`}
            onClick={() => setActiveTab('notifications')}
          >
            Notifications
          </button>
          <button
            className={`settings-tabs__tab ${activeTab === 'security' ? 'settings-tabs__tab--active' : ''}`}
            onClick={() => setActiveTab('security')}
          >
            Security
          </button>
        </div>

        {/* Messages */}
        {message && (
          <div className="settings-message settings-message--success">
            <svg className="settings-message__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M22 11.08V12a10 10 0 11-5.93-9.14" />
              <polyline points="22 4 12 14.01 9 11.01" />
            </svg>
            {message}
          </div>
        )}

        {error && (
          <div className="settings-message settings-message--error">
            <svg className="settings-message__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <line x1="15" y1="9" x2="9" y2="15" />
              <line x1="9" y1="9" x2="15" y2="15" />
            </svg>
            {error}
          </div>
        )}

        {/* General Tab */}
        {activeTab === 'general' && (
          <div className="settings-card">
            <div className="settings-card__header">
              <h2 className="settings-card__title">Profile Information</h2>
              <p className="settings-card__subtitle">Update your account details</p>
            </div>

            {/* Success/Error Messages */}
            {message && (
              <div className="settings-message settings-message--success">
                <svg className="settings-message__icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                {message}
              </div>
            )}
            {error && (
              <div className="settings-message settings-message--error">
                <svg className="settings-message__icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
                {error}
              </div>
            )}

            {/* Profile Section */}
            {user && (
              <>
                <div className="settings-profile">
                  <div className="settings-profile__avatar">
                    {user.profile_photo ? (
                      <img
                        src={user.profile_photo}
                        alt={user.username}
                        className="settings-profile__avatar-img"
                      />
                    ) : (
                      getInitials(user.username)
                    )}
                  </div>
                  <div className="settings-profile__actions">
                    <input
                      type="file"
                      id="photo-upload"
                      accept="image/png,image/jpeg,image/jpg,image/gif,image/webp"
                      onChange={handlePhotoUpload}
                      style={{ display: 'none' }}
                    />
                    <button
                      className="settings-profile__btn settings-profile__btn--outline"
                      onClick={() => document.getElementById('photo-upload').click()}
                      disabled={photoLoading}
                    >
                      {photoLoading ? 'Uploading...' : 'Upload Photo'}
                    </button>
                    <button
                      className="settings-profile__btn settings-profile__btn--danger"
                      onClick={handlePhotoRemove}
                      disabled={photoLoading || !user.profile_photo}
                    >
                      Remove
                    </button>
                  </div>
                  <p className="settings-form__hint" style={{ marginTop: '8px' }}>
                    Max file size: 1MB. Supported formats: PNG, JPG, GIF, WebP
                  </p>
                </div>

                <form onSubmit={handleUpdateEmail}>
                  <div className="settings-form">
                    <div className="settings-form__group">
                      <label className="settings-form__label">Username</label>
                      <input
                        type="text"
                        value={user.username}
                        disabled
                        className="settings-form__input settings-form__input--readonly"
                      />
                      <span className="settings-form__hint">Username cannot be changed</span>
                    </div>

                    <div className="settings-form__group">
                      <label className="settings-form__label">Email Address</label>
                      <input
                        type="email"
                        value={newEmail}
                        onChange={(e) => setNewEmail(e.target.value)}
                        required
                        className="settings-form__input"
                      />
                    </div>

                    <div className="settings-form__group">
                      <label className="settings-form__label">Account Created</label>
                      <input
                        type="text"
                        value={new Date(user.created_at).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        })}
                        disabled
                        className="settings-form__input settings-form__input--readonly"
                      />
                    </div>

                    <div className="settings-form__group">
                      <label className="settings-form__label">Plan</label>
                      <input
                        type="text"
                        value="Free Plan"
                        disabled
                        className="settings-form__input settings-form__input--readonly"
                      />
                    </div>
                  </div>

                  <div style={{ marginTop: '24px' }}>
                    <button
                      type="submit"
                      disabled={emailLoading || newEmail === user.email}
                      className="settings-btn settings-btn--primary"
                    >
                      {emailLoading ? 'Saving...' : 'Save Changes'}
                    </button>
                  </div>
                </form>

                {/* Persona Selection */}
                <div className="settings-card__header" style={{ marginTop: '32px', borderTop: '1px solid #e5e7eb', paddingTop: '24px' }}>
                  <h2 className="settings-card__title">Briefing Persona</h2>
                  <p className="settings-card__subtitle">Choose how your daily briefings are tailored</p>
                </div>

                <div className="settings-persona">
                  {PERSONA_OPTIONS.map((option) => (
                    <label
                      key={option.value}
                      className={`settings-persona__option ${persona === option.value ? 'settings-persona__option--selected' : ''}`}
                    >
                      <input
                        type="radio"
                        name="persona"
                        value={option.value}
                        checked={persona === option.value}
                        onChange={(e) => handlePersonaChange(e.target.value)}
                        disabled={personaLoading}
                        className="settings-persona__radio"
                      />
                      <div className="settings-persona__content">
                        <div className="settings-persona__label">{option.label}</div>
                        <div className="settings-persona__description">{option.description}</div>
                      </div>
                      {persona === option.value && (
                        <svg className="settings-persona__check" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M22 11.08V12a10 10 0 11-5.93-9.14" />
                          <polyline points="22 4 12 14.01 9 11.01" />
                        </svg>
                      )}
                    </label>
                  ))}
                </div>
                {personaLoading && (
                  <p className="settings-form__hint" style={{ marginTop: '12px' }}>Updating persona...</p>
                )}
              </>
            )}
          </div>
        )}

        {/* Notifications Tab */}
        {activeTab === 'notifications' && (
          <div className="settings-card">
            <div className="settings-card__header">
              <h2 className="settings-card__title">Notification Preferences</h2>
              <p className="settings-card__subtitle">
                Choose what updates you want to receive
                {preferencesLoading && <span style={{ marginLeft: '8px', color: '#6b7280' }}>(Saving...)</span>}
              </p>
            </div>

            <div className="settings-toggle">
              <input
                type="checkbox"
                checked={preferences.email_notifications}
                onChange={() => handlePreferenceChange('email_notifications')}
                disabled={preferencesLoading}
                className="settings-toggle__checkbox"
              />
              <div className="settings-toggle__content">
                <div className="settings-toggle__label">Email notifications</div>
                <div className="settings-toggle__description">
                  Receive updates about new articles and platform features
                </div>
              </div>
            </div>

            <div className="settings-toggle">
              <input
                type="checkbox"
                checked={preferences.breakpoint_alerts}
                onChange={() => handlePreferenceChange('breakpoint_alerts')}
                disabled={preferencesLoading}
                className="settings-toggle__checkbox"
              />
              <div className="settings-toggle__content">
                <div className="settings-toggle__label">Breakpoint alerts</div>
                <div className="settings-toggle__description">
                  Get notified when sentiment breakpoints are detected
                </div>
              </div>
            </div>

            {/* Filter options when breakpoint_alerts is enabled */}
            {preferences.breakpoint_alerts && (
              <div className="settings-filter-box">
                <div className="settings-filter-box__title">
                  Filter breakpoint alerts (optional - leave empty to receive all):
                </div>

                <MultiSelect
                  label="Sectors"
                  options={filterOptions.sectors}
                  selected={preferences.breakpoint_sectors || []}
                  filterType="breakpoint_sectors"
                />

                <MultiSelect
                  label="Regions"
                  options={filterOptions.regions}
                  selected={preferences.breakpoint_regions || []}
                  filterType="breakpoint_regions"
                />

                <MultiSelect
                  label="Countries"
                  options={filterOptions.countries}
                  selected={preferences.breakpoint_countries || []}
                  filterType="breakpoint_countries"
                />
              </div>
            )}

            <div className="settings-toggle">
              <input
                type="checkbox"
                checked={preferences.weekly_summary}
                onChange={() => handlePreferenceChange('weekly_summary')}
                disabled={preferencesLoading}
                className="settings-toggle__checkbox"
              />
              <div className="settings-toggle__content">
                <div className="settings-toggle__label">Weekly summary reports</div>
                <div className="settings-toggle__description">
                  Receive weekly economic analysis summaries every Monday
                </div>
              </div>
            </div>

            <p className="settings-form__hint" style={{ marginTop: '24px', paddingTop: '16px', borderTop: '1px solid #e5e7eb' }}>
              Your preferences are saved automatically
            </p>
          </div>
        )}

        {/* Security Tab */}
        {activeTab === 'security' && (
          <div className="settings-card">
            <div className="settings-card__header">
              <h2 className="settings-card__title">Change Password</h2>
              <p className="settings-card__subtitle">Update your password to keep your account secure</p>
            </div>

            <form onSubmit={handleChangePassword} className="settings-form settings-form--single">
              <div className="settings-form__group">
                <label className="settings-form__label">Current Password</label>
                <input
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  required
                  className="settings-form__input"
                  placeholder="Enter current password"
                />
              </div>

              <div className="settings-form__group">
                <label className="settings-form__label">New Password</label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  minLength="6"
                  className="settings-form__input"
                  placeholder="Enter new password"
                />
                <span className="settings-form__hint">At least 6 characters</span>
              </div>

              <div className="settings-form__group">
                <label className="settings-form__label">Confirm New Password</label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  className="settings-form__input"
                  placeholder="Confirm new password"
                  style={{
                    borderColor: confirmPassword && newPassword !== confirmPassword ? '#dc2626' : undefined
                  }}
                />
                {confirmPassword && newPassword !== confirmPassword && (
                  <span className="settings-form__hint" style={{ color: '#dc2626' }}>
                    Passwords do not match
                  </span>
                )}
              </div>

              <div style={{ marginTop: '8px' }}>
                <button
                  type="submit"
                  disabled={passwordLoading}
                  className="settings-btn settings-btn--primary"
                >
                  {passwordLoading ? 'Updating...' : 'Update Password'}
                </button>
              </div>
            </form>
          </div>
        )}
      </main>
    </div>
  );
}

export default Settings;
